import React, { useState } from 'react';

const SessionHistory = ({ sessions }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getFilteredSessions = () => {
    let filtered = [...sessions];

    if (filter !== 'all') {
      const today = new Date().toLocaleDateString();
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString();
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

      switch (filter) {
        case 'today':
          filtered = filtered.filter(session => session.date === today);
          break;
        case 'yesterday':
          filtered = filtered.filter(session => session.date === yesterday);
          break;
        case 'week':
          filtered = filtered.filter(session => new Date(session.completedAt) >= weekAgo);
          break;
        default:
          break;
      }
    }

    // Sort sessions
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.completedAt) - new Date(a.completedAt);
        case 'duration':
          return b.duration - a.duration;
        case 'subject':
          return a.subject.localeCompare(b.subject);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const getUniqueSubjects = () => {
    const subjects = sessions.map(session => session.subject);
    return [...new Set(subjects)];
  };

  const filteredSessions = getFilteredSessions();

  return (
    <div className="session-history">
      <div className="history-header">
        <h2>Study History</h2>
        <div className="history-stats">
          <div className="stat">
            <span className="stat-number">{sessions.length}</span>
            <span className="stat-label">Total Sessions</span>
          </div>
          <div className="stat">
            <span className="stat-number">
              {formatDuration(sessions.reduce((total, session) => total + session.duration, 0))}
            </span>
            <span className="stat-label">Total Time</span>
          </div>
        </div>
      </div>

      <div className="history-controls">
        <div className="filter-controls">
          <label>Filter:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
          </select>
        </div>

        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Date</option>
            <option value="duration">Duration</option>
            <option value="subject">Subject</option>
          </select>
        </div>
      </div>

      {filteredSessions.length === 0 ? (
        <div className="no-sessions">
          <p>No study sessions found.</p>
          <p>Start your first session using the Timer tab!</p>
        </div>
      ) : (
        <div className="sessions-list">
          {filteredSessions.map((session) => (
            <div key={session.id} className="session-item">
              <div className="session-main">
                <div className="session-subject">{session.subject}</div>
                <div className="session-duration">{formatDuration(session.duration)}</div>
              </div>
              <div className="session-details">
                <div className="session-date">
                  {new Date(session.completedAt).toLocaleDateString()}
                </div>
                <div className="session-time">
                  {new Date(session.completedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {getUniqueSubjects().length > 0 && (
        <div className="subjects-summary">
          <h3>Subjects Studied</h3>
          <div className="subjects-list">
            {getUniqueSubjects().map((subject, index) => {
              const subjectSessions = sessions.filter(s => s.subject === subject);
              const totalTime = subjectSessions.reduce((total, session) => total + session.duration, 0);
              return (
                <div key={index} className="subject-item">
                  <span className="subject-name">{subject}</span>
                  <span className="subject-stats">
                    {subjectSessions.length} sessions • {formatDuration(totalTime)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistory;